import React, { createContext, useContext, useState, useEffect } from "react";
import logger from "../utils/logger";

const APIContext = createContext();

export const useAPI = () => {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error("useAPI must be used within an APIProvider");
  }
  return context;
};

// API Client class (converted from original api-client.js)
// Exported for tests and so Fast Refresh can rebind the live instance.
export class APIClient {
  constructor() {
    this.baseURL = this.getApiBaseUrl();

    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
    this.token = this.getAuthToken();
    this.activeProfileId = null;
    this.storyCache = new Map();
    this.audiobookCache = new Map();
    this.contentCacheTtlMs = 5 * 60 * 1000;
    logger.log("🌐 APIClient initialized with backend:", this.baseURL);
  }

  getApiBaseUrl() {
    // Production API URL; REACT_APP_API_URL points a local dev server at a local backend
    return process.env.REACT_APP_API_URL || "https://api.pixleyai.com";
  }

  getAuthToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");

    // A ?token= on a share/content route is a SHARE-access token consumed by
    // the page (StoryDetail / CharacterDetail / AudiobookPlayer), NOT an auth
    // magic-link. Don't capture it as auth or strip it from the URL — doing so
    // both broke shared links (token gone before the page reads it) and
    // corrupted the viewer's session (share token saved as pixley_auth_token).
    const path =
      (typeof window !== "undefined" && window.location.pathname) || "";
    const isShareOrContentRoute =
      /^\/(share|stories|characters|episodes|audiobooks|storybooks)\//.test(
        path,
      );

    if (urlToken && !isShareOrContentRoute) {
      localStorage.setItem("pixley_auth_token", urlToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      return urlToken;
    }

    return localStorage.getItem("pixley_auth_token");
  }

  setBaseUrl(newBaseUrl) {
    this.baseURL = newBaseUrl;
    logger.log("🔄 API base URL updated to:", newBaseUrl);
  }

  setActiveProfileId(profileId) {
    this.activeProfileId = profileId || null;
  }

  getCachedById(cacheMap, key) {
    const cached = cacheMap.get(key);
    if (!cached) return null;
    if (Date.now() - cached.ts > this.contentCacheTtlMs) {
      cacheMap.delete(key);
      return null;
    }
    return cached.value;
  }

  setCachedById(cacheMap, key, value) {
    cacheMap.set(key, { value, ts: Date.now() });
  }

  getSessionCached(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.value || !parsed?.ts) return null;
      if (Date.now() - parsed.ts > this.contentCacheTtlMs) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    } catch {
      return null;
    }
  }

  setSessionCached(key, value) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          value,
          ts: Date.now(),
        }),
      );
    } catch {
      // Ignore storage failures (private mode/quota issues).
    }
  }

  chunkArray(list, size = 6) {
    const chunks = [];
    for (let i = 0; i < list.length; i += size) {
      chunks.push(list.slice(i, i + size));
    }
    return chunks;
  }

  getHeaders(includeAuth = true, profileId = null) {
    const headers = { ...this.defaultHeaders };

    if (includeAuth) {
      // Always get fresh token from localStorage (not stale this.token)
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        logger.log("🔐 Auth token attached:", !!token);
      } else {
        logger.warn("⚠️ No auth token available for request");
      }

      // Profile-scoped backend contract: always send active profile via header
      // when one is selected. Backend falls back to default profile otherwise
      // — so a caller that already knows the profile passes it explicitly
      // rather than trusting this.activeProfileId to be in sync yet.
      const activeProfileId = profileId || this.activeProfileId;
      if (activeProfileId) {
        headers["X-Profile-Id"] = activeProfileId;
      }
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // profileId is ours, not fetch's — pull it out before building the config.
    const { profileId, ...fetchOptions } = options;

    // Build headers
    const headers = {
      ...this.getHeaders(options.includeAuth !== false, profileId),
      ...options.headers,
    };

    // If Content-Type is explicitly set to undefined, delete it (for FormData)
    if (options.headers && options.headers["Content-Type"] === undefined) {
      delete headers["Content-Type"];
    }

    const config = {
      ...fetchOptions,
      credentials: "include",
      headers,
    };

    try {
      logger.log(`🌐 API Request: ${config.method || "GET"} ${url}`);
      logger.log(`🔧 Request config:`, config);

      const response = await fetch(url, config);

      logger.log(
        `📡 Response status: ${response.status} ${response.statusText}`,
      );

      // Check content type before parsing
      const contentType = response.headers.get("content-type");
      const isJson = contentType && contentType.includes("application/json");

      if (!response.ok) {
        // Handle token expiration (401 Unauthorized)
        if (response.status === 401) {
          logger.warn(
            "🔐 Token expired or invalid, handling authentication failure",
          );
          this.handleTokenExpiration();
        }

        let errorData = {};
        try {
          // Try to parse as JSON first
          const text = await response.text();
          try {
            errorData = JSON.parse(text);
          } catch (e) {
            // Not JSON, use text as error message
            logger.error(`❌ Non-JSON error response:`, text.substring(0, 200));
            errorData = {
              detail: text.substring(0, 200) || response.statusText,
            };
          }
        } catch (e) {
          logger.error(`❌ Failed to read error response:`, e);
          errorData = { detail: response.statusText || "Unknown error" };
        }
        logger.error(`❌ API Error Response:`, errorData);

        // For SAFETY_BLOCK errors, preserve the full error data
        if (errorData.status === "SAFETY_BLOCK") {
          const safetyError = new Error(
            errorData.message || "Content safety check failed",
          );
          safetyError.data = errorData; // Preserve full error data
          throw safetyError;
        }

        // Entitlement gate (web trial cutover): the backend's 403 backstop
        // for any creation path the client-side gates miss. FastAPI wraps
        // structured detail objects as {"detail": {code, message, context}}.
        // Broadcast so the trial wall opens and AuthContext re-resolves;
        // gate sites check error.code to suppress their generic toasts.
        if (
          response.status === 403 &&
          errorData?.detail?.code === "SUBSCRIPTION_REQUIRED"
        ) {
          window.dispatchEvent(
            new CustomEvent("pixley:subscription-required", {
              detail: errorData.detail,
            }),
          );
          const subError = new Error(
            errorData.detail.message || "Subscription required",
          );
          subError.code = "SUBSCRIPTION_REQUIRED";
          subError.data = errorData.detail;
          throw subError;
        }

        const httpError = new Error(
          errorData.detail ||
            errorData.error ||
            errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
        );
        httpError.status = response.status;
        throw httpError;
      }

      // For successful responses, parse JSON
      let data;
      try {
        const text = await response.text();
        if (!isJson && text.trim().startsWith("<")) {
          // HTML response detected
          logger.error(
            `❌ Server returned HTML instead of JSON:`,
            text.substring(0, 500),
          );
          throw new Error(
            `Server returned HTML instead of JSON. This usually means the endpoint doesn't exist or returned an error page.`,
          );
        }
        data = JSON.parse(text);
      } catch (parseError) {
        logger.error(`❌ Failed to parse JSON response:`, parseError);
        logger.error(`❌ Response content-type:`, contentType);
        throw new Error(
          `Failed to parse server response as JSON: ${parseError.message}`,
        );
      }
      logger.log(`✅ API Response: ${config.method || "GET"} ${url}`, data);
      return data;
    } catch (error) {
      logger.error(`❌ API Error: ${config.method || "GET"} ${url}`, error);

      // For SAFETY_BLOCK errors, preserve the full error data
      if (error.data && error.data.status === "SAFETY_BLOCK") {
        return {
          success: false,
          status: "SAFETY_BLOCK",
          error: error.message,
          message: error.data.message,
        };
      }

      return { success: false, error: error.message };
    }
  }

  // Auth methods
  async signIn(email, password) {
    return this.request("/auth/signin", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      includeAuth: false,
    });
  }

  async signUp(email, password, name, referrerId = null) {
    const body = { email, password, name };

    // Add referrer_user_id only if referral exists
    if (referrerId) {
      body.referrer_user_id = referrerId;
    }

    return this.request("/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
      includeAuth: false,
    });
  }

  // Google OAuth methods (copied from HTML version)
  // frontendUrl is where Supabase sends the browser back after Google auth
  // (its redirect_to). Defaults to the site origin; the web funnel passes its
  // own /funnel-auth URL so sign-in lands straight back on the funnel page.
  async signInWithGoogle(frontendUrl = window.location.origin) {
    // Generate PKCE parameters for secure OAuth flow
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateRandomState();

    // Store PKCE parameters for later verification
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("oauth_state", state);

    // Send PKCE parameters, redirect URI, and state to backend
    const response = await this.request("/auth/google/signin", {
      method: "POST",
      body: JSON.stringify({
        code_challenge: codeChallenge,
        frontend_url: frontendUrl,
        redirect_uri: frontendUrl, // Where Supabase redirects post-auth
        state: state, // Include state for security
      }),
      includeAuth: false,
    });

    // Redirect user to the OAuth URL returned by backend
    if (response.status && response.data && response.data.url) {
      logger.log("🔗 OAuth URL from backend:", response.data.url);
      logger.log("🎯 Expected redirect_uri in URL:", window.location.origin);
      window.location.href = response.data.url;
    }

    return response;
  }

  // Generate a random code verifier for PKCE
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Generate code challenge from verifier using SHA-256
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest("SHA-256", data);

    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Generate random state parameter for OAuth security
  generateRandomState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // RevenueCat Web Funnel authentication step: exchange the funnel's
  // redirect_uri + state for the callback URL carrying our app_user_id
  // `attribution` is optional ad-click context ({ ttclid, ttp } from
  // utils/tiktokPixel) the backend stores for server-side conversion events.
  async completeFunnelAuth(redirectUri, state, attribution = {}) {
    return this.request("/auth/funnel/callback", {
      method: "POST",
      body: JSON.stringify({ redirect_uri: redirectUri, state, ...(attribution || {}) }),
    });
  }

  // In-house checkout funnel (/funnel-auth): the ad click
  // and UTMs from the landing URL, stored against the signed-in account so
  // the backend's server-side conversions (TikTok Events API, Meta
  // Conversions API, Google Ads) can match the purchase to the ad.
  // `attribution` comes from utils/adAttribution.funnelAttribution().
  // The "finish setting up Pixley" email's pay-link token: whether it is
  // good, and the character the paywall shows for it. Public: nobody is
  // signed in on the device the email is read on.
  async resolveFunnelOffer(offerToken) {
    return this.request("/funnel/offer", {
      method: "POST",
      body: JSON.stringify({ offer_token: offerToken }),
    });
  }

  async recordFunnelAttribution(attribution = {}) {
    return this.request("/auth/funnel/attribution", {
      method: "POST",
      body: JSON.stringify(attribution || {}),
    });
  }

  // Plans for sale on the web (Pro + Family), the live trial / intro-offer
  // terms and the Stripe publishable key for Embedded Checkout. Public.
  async getSubscriptionPlans() {
    return this.request("/subscription-plans");
  }

  // Stripe Checkout for the funnel's own paywall step. `embedded` asks for an
  // Embedded Checkout session (client_secret, card form rendered inside our
  // page, no redirect on completion); otherwise a hosted session URL comes
  // back, which the page uses as its fallback. Ad attribution is already on
  // the RevenueCat Customer (recordFunnelAttribution at sign-in); only the
  // gclid goes with the session.
  // The funnel's `?promo=CODE`: whether the Stripe promotion code applies to
  // Pro and what the first period then costs. Public.
  async getFunnelPromo(code) {
    return this.request(`/funnel/promo?code=${encodeURIComponent(code || "")}`);
  }

  async createFunnelCheckoutSession({
    userId,
    userEmail,
    tier = "pro",
    billingPeriod = "monthly",
    embedded = true,
    attribution = {},
    promoCode = null,
    returnUrl = null,
    // The paywall offer experiment arm the page showed (hooks/useOfferVariant),
    // and, in the pay-what-you-want arm, the first month's price the parent
    // named in cents. The backend prices from its own table for the arm and
    // accepts only a price the chooser offers.
    offerVariant = null,
    chosenCents = null,
    // The "finish setting up Pixley" email's pay-link token
    // (utils/funnelOffer): the backend creates the session for the account
    // the token names, in the pay-what-you-want arm, with nobody signed in;
    // userId / userEmail are then not needed
    offerToken = null,
  }) {
    const body = {
      tier,
      billing_period: billingPeriod,
      // The funnel's own first-period coupons (backend FUNNEL_INTRO_OFFERS)
      source: "funnel",
    };
    if (userId) body.user_id = userId;
    if (userEmail) body.user_email = userEmail;
    // gclid rides into the session metadata for the Google Ads offline
    // conversion upload (as it always has from the web paywall)
    if (attribution?.gclid) body.gclid = attribution.gclid;
    // A promotion code from the funnel URL (replaces the funnel coupon;
    // the backend decides whether it applies)
    if (promoCode) body.promo_code = promoCode;
    if (offerVariant) body.offer_variant = offerVariant;
    if (Number.isInteger(chosenCents)) body.chosen_cents = chosenCents;
    if (offerToken) body.offer_token = offerToken;
    if (embedded) {
      body.ui_mode = "embedded";
    } else {
      // Back to one of our own pages only (a real origin check, not a
      // prefix match); Stripe fills in the session id so the page can have
      // the server confirm the payment before it shows the welcome.
      const origin = window.location.origin;
      let base = `${origin}/funnel-auth`;
      try {
        const parsed = new URL(returnUrl || "", origin);
        if (parsed.origin === origin) base = parsed.toString();
      } catch (_) {
        // keep the default
      }
      const sep = base.includes("?") ? "&" : "?";
      body.success_url = `${base}${sep}checkout=success&session_id={CHECKOUT_SESSION_ID}`;
      body.cancel_url = `${base}${sep}checkout=cancelled`;
    }
    return this.request("/create-stripe-checkout-session", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // The outcome of a Checkout Session this account started (status,
  // payment_status, plan), for the success screen.
  // `offerToken`: the email pay-link's proof that the session is for the
  // account it names (that checkout was made with nobody signed in); it goes
  // in the body of its own endpoint, never in the URL
  async getCheckoutSession(sessionId, { offerToken = null } = {}) {
    if (offerToken) {
      return this.request("/funnel/checkout-session", {
        method: "POST",
        body: JSON.stringify({ offer_token: offerToken, session_id: sessionId }),
      });
    }
    return this.request(`/checkout-session/${encodeURIComponent(sessionId)}`);
  }

  // ── Funnel onboarding: pre-sign-in character preview on web ──────────────
  // Browsers can't do App Attest, so /previewCharacterImage accepts a
  // short-lived HMAC token minted by this endpoint (tightly IP rate-limited).
  // Tokens are only minted mid-funnel: the RevenueCat redirect_uri + state are
  // required — or, for the in-house checkout funnel, which has no RevenueCat
  // params, the page's own random `funnelSession` id, which scopes the
  // per-session budget the same way. Minting is unchallenged — spend is capped
  // per IP (and per funnel session) when the token is actually redeemed for a
  // generation.
  async getFunnelPreviewToken(redirectUri, state, { funnelSession } = {}) {
    // Raw fetch (NOT this.request) so the HTTP status survives on failure — the
    // funnel-auth page needs to tell 429 (out of previews → tell the user to
    // wait) apart from everything else. this.request swallows every error into
    // { success:false } with no status.
    const body = redirectUri && state
      ? { redirect_uri: redirectUri, state }
      : { funnel_session: funnelSession };
    const response = await fetch(`${this.baseURL}/funnel/preview-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let detail = "Could not start character generation. Please try again.";
      try {
        const err = await response.json();
        detail = err.detail || err.error || err.message || detail;
      } catch {
        // non-JSON error body
      }
      const error = new Error(detail);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  // Stateless character generation (same endpoint iOS onboarding uses):
  // sends the drawing/photo/description, returns the generated PNG as a Blob.
  async previewCharacterImageWeb({
    name,
    image,
    prompt,
    handdrawn = false,
    previewToken,
  }) {
    const formData = new FormData();
    const throwawayId = crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}`;
    formData.append("user_id", throwawayId);
    formData.append("name", name || "Preview Character");
    if (prompt) formData.append("prompt", prompt);
    if (image) formData.append("image", image, "character.png");
    formData.append("handdrawn", handdrawn.toString());

    const response = await fetch(`${this.baseURL}/previewCharacterImage`, {
      method: "POST",
      headers: { "x-web-preview-token": previewToken || "" },
      body: formData,
    });

    if (!response.ok) {
      let detail =
        "We couldn't bring your character to life. Please try again.";
      try {
        const err = await response.json();
        detail = err.message || err.detail || detail;
      } catch {
        // non-JSON error body
      }
      const error = new Error(detail);
      // Carried so the caller can skip its auto-retry on 429 (per-IP or
      // per-session preview budget spent — retrying can't succeed).
      error.status = response.status;
      throw error;
    }
    return response.blob();
  }

  // Save the onboarding preview as a real character after sign-in — the same
  // legacy multipart path the iOS onboarding uses (animated_image is uploaded
  // as-is; nothing is regenerated). The source photo is not stored.
  async saveFunnelCharacter({ userId, name, animatedImage }) {
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("name", name);
    formData.append("animated_image", animatedImage, "animated.png");
    return this.uploadFileToEndpoint("/createCharacter", formData);
  }

  async signOut() {
    const result = await this.request("/auth/signout", { method: "POST" });
    if (result.status) {
      this.clearToken();
    }
    return result;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("pixley_auth_token");
  }

  // Handle token expiration - clear auth state and redirect to landing page
  handleTokenExpiration() {
    logger.warn("🔐 Handling token expiration...");

    // Clear the expired token
    this.clearToken();

    // Clear all authentication related data
    localStorage.removeItem("pixley_auth_token");
    localStorage.removeItem("pixley_user_data");

    // Trigger a custom event that the AuthContext can listen to
    window.dispatchEvent(
      new CustomEvent("tokenExpired", {
        detail: { message: "Your session has expired. Please sign in again." },
      }),
    );

    logger.log("✅ Token expiration handled, event dispatched");
  }

  async getSession() {
    return this.request("/auth/session");
  }

  async exchangeSupabaseSession(accessToken, refreshToken) {
    return this.request("/auth/exchange-supabase-session", {
      method: "POST",
      body: JSON.stringify({
        access_token: accessToken,
        refresh_token: refreshToken,
      }),
      includeAuth: false,
    });
  }

  async exchangeAuthCodeForTokens(
    code,
    codeVerifier,
    redirectUri = window.location.origin,
  ) {
    const body = {
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
    };

    const referrerId = localStorage.getItem("pixley_referral_id");

    console.log("🧠 OAUTH EXCHANGE DEBUG");
    console.log("Stored referral in localStorage:", referrerId);
    console.log("Full OAuth payload BEFORE sending:", {
      ...body,
      referrer_user_id: referrerId,
    });

    if (referrerId) {
      body.referrer_user_id = referrerId;
      console.log("✅ Referral added to request body");
    } else {
      console.log("🚫 No referral found in localStorage");
    }

    return this.request("/auth/exchange-auth-code", {
      method: "POST",
      body: JSON.stringify(body),
      includeAuth: false,
    });
  }

  async exchangeEmailAuthToken(token) {
    try {
      const formData = new FormData();
      formData.append("token", token);
      const response = await fetch(
        `${this.baseURL}/auth/email-token-exchange`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }
      return data;
    } catch (error) {
      logger.error("Email token exchange error:", error);
      throw error;
    }
  }

  async verifyToken() {
    return this.request("/auth/verify");
  }

  // Referral stats
  async getReferralStats() {
    return this.request("/api/referrals/stats");
  }

  // Health check
  async healthCheck() {
    return this.request("/health", { includeAuth: false });
  }

  // Character methods

  async getUserCharacters(userId, profileId = null) {
    // Scope to the caller's profile explicitly: the backend falls back to the
    // account's DEFAULT profile when X-Profile-Id is absent, so a request that
    // beats ProfileContext's header sync would return the wrong profile's cast.
    return this.request(`/users/${userId}/characters`, { profileId });
  }

  async getCharacter(id, token = null) {
    // Owner fetch attaches JWT by default; a share token (?token=) lets
    // non-owners open a shared character. Backend requires one or the other.
    const url = token
      ? `/characters/${id}?token=${encodeURIComponent(token)}`
      : `/characters/${id}`;
    return this.request(url);
  }

  // Enable/disable sharing for a character (owner only). Returns { share_token }
  // used to build the /share/character/{id}?token=… link; null when disabled.
  async setCharacterSharing(id, shared) {
    return this.request(`/characters/${id}/share`, {
      method: "PUT",
      body: JSON.stringify({ shared: Boolean(shared) }),
    });
  }

  async createCharacter(characterData) {
    const safeName =
      typeof characterData.name === "string" && characterData.name.trim()
        ? characterData.name.trim()
        : "Random Character";
    const payload = {
      user_id: characterData.user_id,
      profile_id: characterData.profile_id,
      preview_id: characterData.preview_id,
      name: safeName,
    };
    // Voice picked at creation: preset (with id), random, or cloned (the
    // recording uploads right after the character exists).
    if (characterData.voice_choice?.type) {
      payload.voice_choice_type = characterData.voice_choice.type;
      if (characterData.voice_choice.voice_id) payload.voice_choice_voice_id = characterData.voice_choice.voice_id;
    }
    console.log("CREATE CHARACTER PAYLOAD:", payload);
    return this.request("/createCharacter", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Create a character from ALREADY-generated images (onboarding avatar
  // flow). The JSON createCharacter above is preview_id-only and silently
  // drops files; the backend's multipart path accepts them directly.
  async createCharacterFromImages({ user_id, name, handdrawn_image, animated_image }) {
    const formData = new FormData();
    formData.append("user_id", user_id);
    formData.append("name", name && name.trim() ? name.trim() : "My Character");
    if (handdrawn_image) formData.append("handdrawn_image", handdrawn_image);
    if (animated_image) formData.append("animated_image", animated_image);
    return this.uploadFileToEndpoint("/createCharacter", formData);
  }

  // Character voices (core/voice_catalog on the backend)
  async getVoiceOptions() {
    return this.request("/characters/voice-options");
  }

  // Vision shortlist for a character picture: { suggested, alternatives }.
  async getVoiceSuggestions(imageUrl) {
    const formData = new FormData();
    formData.append("image_url", imageUrl);
    return this.uploadFileToEndpoint("/characters/voice-suggestions", formData);
  }

  // Change a character's voice to a catalog voice (10 stars; the backend charges).
  async setCharacterVoice(characterId, voiceId) {
    const formData = new FormData();
    formData.append("voice_id", voiceId);
    return this.uploadFileToEndpoint(`/characters/${characterId}/voice/preset`, formData);
  }

  // Clone the family's recording into the character's voice (first recording
  // free, 10 stars after; the backend decides). audioFiles: File[] (.webm/.m4a/.mp3/.wav, 5s+ each).
  async cloneCharacterVoice(characterId, audioFiles) {
    const formData = new FormData();
    audioFiles.forEach((f) => formData.append("audio_files", f));
    return this.uploadFileToEndpoint(`/characters/${characterId}/voice/clone`, formData);
  }

  async createCharacterWithAnimation({
    user_id,
    name,
    image,
    handdrawn = false,
    prompt,
    voice_choice,
  }) {
    const formData = new FormData();
    const safeName =
      typeof name === "string" && name.trim()
        ? name.trim()
        : "Random Character";
    formData.append("user_id", user_id);
    formData.append("name", safeName);
    formData.append("handdrawn", handdrawn.toString());
    if (image) formData.append("image", image);
    if (prompt) formData.append("prompt", prompt);
    // voice_choice: { type: 'preset', voice_id } | { type: 'random' } | { type: 'cloned' }
    if (voice_choice?.type) {
      formData.append("voice_choice_type", voice_choice.type);
      if (voice_choice.voice_id) formData.append("voice_choice_voice_id", voice_choice.voice_id);
    }
    return this.uploadFileToEndpoint("/createCharacterWithAnimation", formData);
  }

  async getCharacterStatus(characterId) {
    return this.request(`/characterStatus/${characterId}`);
  }

  async updateCharacter(id, characterData) {
    // Create FormData for backend that expects form fields
    const formData = new FormData();

    // Add each field to FormData
    if (characterData.name !== undefined) {
      formData.append("name", characterData.name);
    }
    if (characterData.description !== undefined) {
      formData.append("description", characterData.description);
    }
    if (characterData.handdrawn_image) {
      formData.append("handdrawn_image", characterData.handdrawn_image);
    }
    if (characterData.animated_image) {
      formData.append("animated_image", characterData.animated_image);
    }

    // Use uploadFileToEndpoint for form data handling
    return this.uploadFileToEndpoint(`/characters/${id}`, formData, {
      method: "PUT",
    });
  }

  async deleteCharacter(id) {
    return this.request(`/characters/${id}`, {
      method: "DELETE",
    });
  }

  async enhanceCharacter(id) {
    return this.request(`/characters/${id}/enhance`, {
      method: "POST",
    });
  }

  async animateImageData(imageFile, isHanddrawn = true, user_id) {
    if (!user_id) {
      console.error("❌ Missing user_id");
      throw new Error("Missing user_id");
    }
    const formData = new FormData();
    formData.append("user_id", user_id);
    formData.append("name", "Random Character");
    // Send both keys for backend compatibility across preview handlers.
    formData.append("file", imageFile);
    formData.append("image", imageFile);
    formData.append("handdrawn", isHanddrawn.toString());
    console.log("FORM DATA:", [...formData.entries()]);

    // Character preview flow now uses previewCharacter endpoint.
    return this.uploadFileToEndpoint("/previewCharacter", formData);
  }

  async previewCharacter(imageFile, isHanddrawn = true, user_id) {
    if (!user_id) {
      console.error("❌ Missing user_id");
      throw new Error("Missing user_id");
    }
    const formData = new FormData();
    formData.append("user_id", user_id);
    formData.append("name", "Random Character");
    // Send both keys for backend compatibility across preview handlers.
    formData.append("file", imageFile);
    formData.append("image", imageFile);
    formData.append("handdrawn", isHanddrawn.toString());
    console.log("FORM DATA:", [...formData.entries()]);
    return this.uploadFileToEndpoint("/previewCharacter", formData);
  }

  async previewCharacterFromPrompt({
    user_id,
    prompt,
    image,
    handdrawn = false,
    name,
  }) {
    if (!user_id) {
      console.error("❌ Missing user_id");
      throw new Error("Missing user_id");
    }
    const formData = new FormData();
    const generatedName =
      (typeof prompt === "string" && prompt.trim()
        ? prompt.trim().split(/\s+/).slice(0, 3).join(" ")
        : "") ||
      (typeof name === "string" && name.trim()) ||
      "Random Character";
    formData.append("user_id", user_id);
    formData.append("name", generatedName);
    if (prompt) formData.append("prompt", prompt);
    if (image) {
      // Send both keys for backend compatibility across preview handlers.
      formData.append("file", image);
      formData.append("image", image);
    }
    formData.append("handdrawn", handdrawn.toString());
    console.log("FORM DATA:", [...formData.entries()]);
    return this.uploadFileToEndpoint("/previewCharacter", formData);
  }

  async animateProfilePicture(imageFile) {
    const formData = new FormData();
    formData.append("file", imageFile);

    // Use new profile picture specific endpoint with accurate prompting
    return this.uploadFileToEndpoint("/animate-profile-picture", formData);
  }

  // Helper method for file uploads (similar to HTML version)
  async uploadFileToEndpoint(endpoint, formData, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Build headers without Content-Type (let browser set it for FormData)
    const headers = { ...this.getHeaders() };
    delete headers["Content-Type"];

    const config = {
      method: "POST",
      credentials: "include",
      body: formData,
      headers: headers,
      ...options,
    };

    try {
      logger.log(`🌐 File Upload Request: POST ${url}`);
      logger.log(`🔧 FormData contents:`, Array.from(formData.entries()));

      const response = await fetch(url, config);

      logger.log(
        `📡 Response status: ${response.status} ${response.statusText}`,
      );

      if (!response.ok) {
        // Handle token expiration (401 Unauthorized) for file uploads too
        if (response.status === 401) {
          logger.warn(
            "🔐 Token expired or invalid during file upload, handling authentication failure",
          );
          this.handleTokenExpiration();
        }

        const errorText = await response.text();
        logger.error(`❌ API Error Response (text):`, errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }

        // Entitlement gate backstop (mirror of the JSON request path):
        // broadcast so the trial wall opens, and throw a typed error so
        // gate sites can suppress their generic toasts.
        if (
          response.status === 403 &&
          errorData?.detail?.code === "SUBSCRIPTION_REQUIRED"
        ) {
          window.dispatchEvent(
            new CustomEvent("pixley:subscription-required", {
              detail: errorData.detail,
            }),
          );
          const subError = new Error(
            errorData.detail.message || "Subscription required",
          );
          subError.code = "SUBSCRIPTION_REQUIRED";
          subError.data = errorData.detail;
          throw subError;
        }

        // Check for SAFETY_BLOCK error (HTTP 400 with status: "SAFETY_BLOCK")
        if (response.status === 400 && errorData.status === "SAFETY_BLOCK") {
          logger.warn("🚫 Safety block detected for image animation");
          return {
            success: false,
            error: "SAFETY_BLOCK",
            isSafetyBlock: true,
          };
        }

        // Check for name_inappropriate error (HTTP 400 with status: "name_inappropriate")
        if (
          response.status === 400 &&
          errorData.status === "name_inappropriate"
        ) {
          logger.warn("🚫 Inappropriate name detected");
          return {
            success: false,
            status: "name_inappropriate",
            error: "name_inappropriate",
            message:
              errorData.message ||
              "This name contains inappropriate content. Please choose a different name.",
          };
        }

        throw new Error(
          errorData.error ||
            errorData.detail ||
            `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      // Try to parse as JSON first, if that fails, handle as blob
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
        logger.log(`✅ File Upload Response (JSON): POST ${url}`, data);
      } else {
        // Handle as blob (for image data)
        data = {
          success: true,
          data: response,
        };
        logger.log(
          `✅ File Upload Response (Blob): POST ${url}`,
          "Binary data received",
        );
      }

      return data;
    } catch (error) {
      logger.error(`❌ File Upload Error: POST ${url}`, error);
      return { success: false, error: error.message };
    }
  }

  // Show methods

  async getUserShows(userId, profileId = null) {
    return this.request(`/users/${userId}/shows`);
  }

  // Generate AI title for shows
  async generateTitle(characterName) {
    const formData = new FormData();
    formData.append("character_name", characterName);
    return this.uploadFileToEndpoint("/generateTitle", formData);
  }

  // Check text safety via Sightengine
  async checkTextSafety(text) {
    const formData = new FormData();
    formData.append("text", text);
    return this.uploadFileToEndpoint("/check-text-safety", formData);
  }

  // Story methods (standalone stories)

  async getStoryTemplates() {
    return this.request("/story-templates");
  }

  async getStoryTopics() {
    return this.request("/story-topics");
  }

  // Second argument is either a duration (the original story-only signature) or
  // an options bag: { targetDuration, medium, pageCount, readingLevel }.
  // Omitting `medium` leaves the server on its story default.
  async sparkStoryTopic(topicKey, options = null) {
    const opts =
      options !== null && typeof options === "object" ? options : { targetDuration: options };

    const params = new URLSearchParams();
    if (opts.targetDuration) params.set("target_duration", opts.targetDuration);
    if (opts.medium) params.set("medium", opts.medium);
    if (opts.pageCount) params.set("page_count", opts.pageCount);
    if (opts.readingLevel) params.set("reading_level", opts.readingLevel);

    const qs = params.toString();
    return this.request(
      `/story-topics/${encodeURIComponent(topicKey)}/spark${qs ? `?${qs}` : ""}`,
      { method: "POST" }
    );
  }

  async getWeeklyChallenge() {
    return this.request("/weekly-challenge");
  }

  async getContinueWatching() {
    return this.request("/continue-watching");
  }

  // Games methods
  async getGames() {
    return this.request("/games");
  }

  async getGamesProgress() {
    return this.request("/games/progress");
  }

  async getGameProgress() {
    return this.getGamesProgress();
  }

  /**
   * GET /learn/progress — per-profile Learn course completions.
   * Requires X-Profile-Id (set via setActiveProfileId / ProfileContext).
   * Returns `{ progress: { v, courses, updated_at } }`.
   */
  async getLearnProgress() {
    return this.request("/learn/progress");
  }

  /**
   * POST /learn/progress — union incoming course IDs into stored map.
   * Body: `{ v: 1, courses: { "letter.a": ["feedAlligator", ...] } }`
   * Server merges by set-union; response is canonical.
   */
  async postLearnProgress(courses, version = 1) {
    return this.request("/learn/progress", {
      method: "POST",
      body: JSON.stringify({ v: version, courses: courses || {} }),
    });
  }

  async postGameProgress(gameType, score, completed, storyId = null) {
    if (!gameType) {
      return { status: false, error: "Game type is required" };
    }

    const payload = {
      game_type: gameType,
      score,
      completed: Boolean(completed),
      story_id: storyId,
    };

    logger.log("🎮 POST /games/progress", payload);

    return this.request("/games/progress", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async saveGameProgress(gameType, score, completed, storyId = null) {
    return this.postGameProgress(gameType, score, completed, storyId);
  }

  async getUserStories(profileId = null) {
    // Backend gets user from auth token and active profile from X-Profile-Id
    return this.request("/stories", { profileId });
  }

  async getStory(id, accessToken = null, skipCache = false) {
    const cacheKey = accessToken ? `${id}:token` : id;
    // Authenticated reads skip cache so internal users always receive a fresh
    // can_moderate_storyverse / show_in_storyverse decision from the backend.
    const hasAuth = !!this.getAuthToken();
    if (!skipCache && !hasAuth) {
      const cached = this.getCachedById(this.storyCache, cacheKey);
      if (cached) return cached;
      const sessionCached = this.getSessionCached(`story_cache_${cacheKey}`);
      if (sessionCached) {
        this.setCachedById(this.storyCache, cacheKey, sessionCached);
        return sessionCached;
      }
    }

    // accessToken: Optional magic link token from email for private stories.
    // Always attempt JWT from localStorage so internal moderation fields attach.
    const url = accessToken
      ? `/stories/${id}?token=${accessToken}`
      : `/stories/${id}`;
    const response = await this.request(url, {
      includeAuth: true,
    });
    if (response?.status && response?.data && !hasAuth) {
      this.setCachedById(this.storyCache, cacheKey, response);
      this.setSessionCached(`story_cache_${cacheKey}`, response);
    }
    return response;
  }

  async getStorySubtitles(storyId) {
    if (!storyId) {
      return { status: false, error: "Story id is required" };
    }

    return this.request(`/stories/${storyId}/subtitles`, {
      includeAuth: !!this.token,
    });
  }

  async generateStorySubtitles(storyId, languageCode = "en") {
    if (!storyId) {
      return { status: false, error: "Story id is required" };
    }

    const formData = new FormData();
    formData.append("language_code", languageCode || "en");

    return this.uploadFileToEndpoint(
      `/stories/${storyId}/generate-subtitles`,
      formData,
      { includeAuth: !!this.token },
    );
  }

  async getStoriesByIds(ids = [], options = {}) {
    const normalizedIds = [...new Set(ids.filter(Boolean))];
    if (!normalizedIds.length) return [];

    const chunkSize =
      Number(options.chunkSize) > 0 ? Number(options.chunkSize) : 6;
    const chunks = this.chunkArray(normalizedIds, chunkSize);
    const results = [];

    for (const chunk of chunks) {
      const chunkResults = await Promise.all(
        chunk.map(async (id) => {
          const response = await this.getStory(id);
          if (!response?.status || !response?.data) {
            logger.warn(
              `⚠️ Continue Watching: story not found or failed for id=${id}`,
            );
            return null;
          }
          return response.data;
        }),
      );
      results.push(...chunkResults.filter(Boolean));
    }

    return results;
  }

  async createStory(
    storyData,
    targetDuration = null,
    aspectRatio = "16:9",
    primaryLanguage = "ENGLISH",
    animationStyle = "cgi",
  ) {
    // Convert to FormData like createEpisode
    const formData = new FormData();
    Object.keys(storyData).forEach((key) => {
      if (key === "characters" && Array.isArray(storyData[key])) {
        // Append each character ID separately for List[str] in FastAPI
        storyData[key].forEach((charId) => {
          formData.append("characters", charId);
        });
      } else {
        formData.append(key, storyData[key]);
      }
    });

    // Add premium features (always append all three)
    if (targetDuration) {
      formData.append("target_duration", targetDuration);
    }
    formData.append("aspect_ratio", aspectRatio);
    formData.append("primary_language", primaryLanguage);
    formData.append("animation_style", animationStyle || "cgi");

    return this.uploadFileToEndpoint("/createStory", formData);
  }

  /** Picker options for story animation styles (public; cached by the browser). */
  async getAnimationStyles() {
    return this.request("/stories/animation-styles");
  }

  async updateStory(id, storyData) {
    return this.request(`/stories/${id}`, {
      method: "PUT",
      body: JSON.stringify(storyData),
    });
  }

  async deleteStory(id) {
    return this.request(`/stories/${id}`, {
      method: "DELETE",
    });
  }

  async deleteAudiobook(audiobookId) {
    return this.request(`/audiobooks/${audiobookId}`, {
      method: "DELETE",
    });
  }

  // Audiobook methods
  async createAudiobook(formData) {
    console.log(
      "🎧 CREATE AUDIOBOOK - API Context: Starting request to /audiobooks/create",
    );
    console.log(
      "🎧 CREATE AUDIOBOOK - API Context: FormData entries",
      Array.from(formData.entries()).map(([key, value]) => ({
        key,
        value:
          typeof value === "string"
            ? value.substring(0, 100)
            : `[${typeof value}]`,
      })),
    );

    try {
      const response = await this.uploadFileToEndpoint(
        "/audiobooks/create",
        formData,
      );
      console.log("🎧 CREATE AUDIOBOOK - API Context: Request completed", {
        responseType: typeof response,
        hasSuccess: "success" in response,
        hasStatus: "status" in response,
        hasData: "data" in response,
        responseKeys: response ? Object.keys(response) : [],
        response,
      });
      return response;
    } catch (error) {
      console.log("❌ ERROR CONDITION HIT: API Context createAudiobook catch", {
        errorType: "api_context_error",
        error: error,
        errorMessage: error?.message,
        errorStack: error?.stack,
      });
      throw error;
    }
  }

  async getAudiobook(audiobookId, accessToken = null, skipCache = false) {
    const cached = this.getCachedById(this.audiobookCache, audiobookId);
    if (!skipCache && cached) return cached;
    const sessionCached = this.getSessionCached(
      `audiobook_cache_${audiobookId}`,
    );
    if (!skipCache && sessionCached) {
      this.setCachedById(this.audiobookCache, audiobookId, sessionCached);
      return sessionCached;
    }

    const token = this.getAuthToken();
    console.log("GET audiobook API called");
    logger.log(`🔐 getAudiobook - Auth token attached:`, !!token);
    const url = accessToken
      ? `/audiobooks/${audiobookId}?token=${encodeURIComponent(accessToken)}`
      : `/audiobooks/${audiobookId}`;
    const response = await this.request(url, {
      includeAuth: !!token,
    });
    if (response?.status && response?.data) {
      this.setCachedById(this.audiobookCache, audiobookId, response);
      this.setSessionCached(`audiobook_cache_${audiobookId}`, response);
    }
    return response;
  }

  async getAudiobooksByIds(ids = [], options = {}) {
    const normalizedIds = [...new Set(ids.filter(Boolean))];
    if (!normalizedIds.length) return [];

    const chunkSize =
      Number(options.chunkSize) > 0 ? Number(options.chunkSize) : 6;
    const chunks = this.chunkArray(normalizedIds, chunkSize);
    const results = [];

    for (const chunk of chunks) {
      const chunkResults = await Promise.all(
        chunk.map(async (id) => {
          const response = await this.getAudiobook(id);
          if (!response?.status || !response?.data) {
            logger.warn(
              `⚠️ Continue Listening: audiobook not found or failed for id=${id}`,
            );
            return null;
          }
          return response.data;
        }),
      );
      results.push(...chunkResults.filter(Boolean));
    }

    return results;
  }

  async getUserAudiobooks(userId, profileId = null) {
    return this.request(`/audiobooks/list/${userId}`);
  }

  // ── AI Voice Storybook (Gemini Live) ──

  async createVoiceStorybook({
    characterIds = [],
    language = "en",
    isPublic = false,
  } = {}) {
    const formData = new FormData();
    formData.append("character_ids", characterIds.filter(Boolean).join(","));
    formData.append("language", language);
    formData.append("is_public", String(!!isPublic));
    return this.uploadFileToEndpoint("/audiobooks/voice/create", formData);
  }

  async mintVoiceWsTicket(sessionId) {
    const formData = new FormData();
    if (sessionId) formData.append("session_id", sessionId);
    return this.uploadFileToEndpoint("/audiobooks/voice/ws-ticket", formData);
  }

  async continueVoiceStorybook({ sessionId = null, audiobookId = null } = {}) {
    const formData = new FormData();
    if (sessionId) formData.append("session_id", sessionId);
    if (audiobookId) formData.append("audiobook_id", audiobookId);
    return this.uploadFileToEndpoint("/audiobooks/voice/continue", formData);
  }

  getVoiceWsBaseUrl() {
    return this.baseURL.replace(/^https/i, "wss").replace(/^http/i, "ws");
  }

  /**
   * GET /audiobooks — returns list of audiobooks (e.g. for public/recommended).
   * Frontend filters is_public === true and status === 'ready' for Recommended Storybooks.
   */
  // async getAudiobooks() {
  //   return this.request("/audiobooks");
  // }

  /**
   * GET /audiobooks/public/feed — public storybooks feed for recommendations.
   * Returns { status, data: [{ id, title, thumbnail_url, tags, views, is_public, created_at }], count }.
   * Use this for Recommended Storybooks; do not use GET /audiobooks for recommendations.
   */
  async getPublicStorybooksFeed(limit = 6, offset = 0) {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return this.request(`/audiobooks/public/feed?${params.toString()}`, {
      includeAuth: false,
    });
  }

  async generateAudiobookVideo(audiobookId) {
    return this.request(`/audiobooks/${audiobookId}/generate-video`, {
      method: "POST",
    });
  }

  async updateStoryVisibility(id, isPublic) {
    return this.request(`/stories/${id}/visibility`, {
      method: "PUT",
      body: JSON.stringify({ is_public: isPublic }),
    });
  }

  // Link sharing without changing feed visibility (owner only). Returns
  // { share_token } for the /share/episode/{id}?token=… link; shared:false
  // discards the token (kills links) and forces the story private.
  async setStorySharing(id, shared) {
    return this.request(`/stories/${id}/share`, {
      method: "PUT",
      body: JSON.stringify({ shared: Boolean(shared) }),
    });
  }

  async updateAudiobookVisibility(id, isPublic) {
    const payload = { is_public: Boolean(isPublic) };
    logger.log(`🔐 updateAudiobookVisibility - Payload:`, payload);
    return this.request(`/audiobooks/${id}/visibility`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  // Enable/disable link sharing for a storybook (owner only). Returns
  // { share_token } for the …/audiobooks/{id}?token=… link; null when disabled.
  async setAudiobookSharing(id, shared) {
    return this.request(`/audiobooks/${id}/share`, {
      method: "PUT",
      body: JSON.stringify({ shared: Boolean(shared) }),
    });
  }

  async translateAudiobook(audiobookId, targetLanguage) {
    logger.log(
      `🌍 translateAudiobook - Translating ${audiobookId} to ${targetLanguage}`,
    );
    return this.request(`/audiobooks/${audiobookId}/translate`, {
      method: "POST",
      body: JSON.stringify({ target_language: targetLanguage }),
    });
  }

  /**
   * POST /audiobooks/events — backend expects flat payload only (no data_tags).
   * Payload: user_id, audiobook_id, session_id, event_type, optional metadata (from_sec, to_sec, page_index, etc.).
   */
  async sendAudiobookEvent(
    userId,
    audiobookId,
    sessionId,
    eventType,
    metadata = {},
  ) {
    const payload = {
      user_id: userId,
      audiobook_id: audiobookId,
      session_id: sessionId,
      event_type: eventType,
      ...metadata,
    };

    // Guard: do not send if any required field is missing (prevents duplicate/undefined payloads and backend rejections).
    if (!userId || !audiobookId || !sessionId || !eventType) {
      logger.warn("📊 sendAudiobookEvent: skip — missing required field", {
        hasUserId: !!userId,
        hasAudiobookId: !!audiobookId,
        hasSessionId: !!sessionId,
        eventType: eventType ?? "(undefined)",
      });
      return { status: false, error: "Missing required payload field" };
    }

    logger.log("📊 POST /audiobooks/events", eventType, payload);

    return this.request("/audiobooks/events", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async downloadWatermarkedStory(storyId) {
    const url = `${this.baseURL}/stories/${storyId}/download-watermarked`;
    const headers = this.getHeaders(true);

    try {
      logger.log(`📥 Downloading watermarked story: ${storyId}`);
      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`❌ Download failed: ${response.status}`, errorText);
        throw new Error(`Failed to download story: ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `story_${storyId}_watermarked.mp4`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      logger.log(`✅ Successfully downloaded watermarked story: ${filename}`);
      return { success: true, filename };
    } catch (error) {
      logger.error(`❌ Error downloading watermarked story:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * TEMP testing helper — GET /audiobooks/{id}/pdf
   * Downloads a storybook PDF (premium / downloadAbility gated on backend).
   */
  async downloadAudiobookPdf(audiobookId, title = "storybook", accessToken = null) {
    if (!audiobookId) {
      return { success: false, error: "Storybook id is required" };
    }

    const params = new URLSearchParams();
    if (accessToken) params.set("token", accessToken);
    const qs = params.toString();
    const url = `${this.baseURL}/audiobooks/${audiobookId}/pdf${qs ? `?${qs}` : ""}`;
    const headers = this.getHeaders(true);

    try {
      logger.log(`📥 Downloading storybook PDF: ${audiobookId}`);
      const response = await fetch(url, {
        method: "GET",
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        let detail = response.statusText || "Failed to download PDF";
        try {
          const errBody = await response.json();
          detail = errBody.detail || errBody.error || errBody.message || detail;
        } catch (_) {
          // non-JSON error body
        }
        logger.error(`❌ PDF download failed: ${response.status}`, detail);
        throw new Error(detail);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const safeTitle = String(title || "storybook")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_")
        .toLowerCase()
        .slice(0, 60);
      let filename = `${safeTitle || "storybook"}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      logger.log(`✅ Successfully downloaded storybook PDF: ${filename}`);
      return { success: true, filename };
    } catch (error) {
      logger.error(`❌ Error downloading storybook PDF:`, error);
      return { success: false, error: error.message || "Failed to download PDF" };
    }
  }

  async downloadWatermarkedCharacter(characterId, characterName = "character") {
    const url = `${this.baseURL}/characters/${characterId}/download-watermarked`;
    const headers = this.getHeaders(true);

    try {
      logger.log(`📥 Downloading watermarked character: ${characterId}`);
      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`❌ Download failed: ${response.status}`, errorText);
        throw new Error(`Failed to download character: ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use character name
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `${characterName
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_")
        .toLowerCase()}.png`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      logger.log(
        `✅ Successfully downloaded watermarked character: ${filename}`,
      );
      return { success: true, filename };
    } catch (error) {
      logger.error(`❌ Error downloading watermarked character:`, error);
      return { success: false, error: error.message };
    }
  }

  async downloadWatermarkedAudio(storyId, storyTitle = "story") {
    const url = `${this.baseURL}/stories/${storyId}/download-watermarked-audio`;
    const headers = this.getHeaders(true);

    try {
      logger.log(`📥 Downloading watermarked audio: ${storyId}`);
      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`❌ Download failed: ${response.status}`, errorText);
        throw new Error(`Failed to download audio: ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use story title
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `${storyTitle
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_")
        .toLowerCase()}.mp3`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      logger.log(`✅ Successfully downloaded watermarked audio: ${filename}`);
      return { success: true, filename };
    } catch (error) {
      logger.error(`❌ Error downloading watermarked audio:`, error);
      return { success: false, error: error.message };
    }
  }

  // Generate story ideas (without show_id or theme)
  async generateStoryIdeas(characterIds) {
    return this.request("/generate-episode-description", {
      method: "POST",
      body: JSON.stringify({
        character_ids: characterIds,
      }),
    });
  }

  // Storyverse methods
  async getStoriverseShows() {
    // Public endpoint - no auth required
    // Cache for 5 minutes to improve performance
    const cacheKey = "storyverse_shows";
    const cached = this.getSessionCached(cacheKey);
    if (cached) {
      logger.log("✅ Using cached storyverse shows");
      return cached;
    }

    const result = await this.request("/storyverse/shows", {
      includeAuth: false,
    });
    if (result?.status) {
      this.setSessionCached(cacheKey, result);
    }
    return result;
  }

  async getStoriverseShow(showId) {
    // Public endpoint - no auth required
    return this.request(`/storyverse/shows/${showId}`, { includeAuth: false });
  }

  async getStoriverseEpisodes(showId = null) {
    const endpoint = showId
      ? `/storyverse/shows/${showId}/episodes`
      : "/storyverse/episodes";
    // Public endpoint - no auth required
    return this.request(endpoint, { includeAuth: false });
  }

  async getStoriverseEpisode(episodeId) {
    // Public endpoint - no auth required
    return this.request(`/storyverse/episodes/${episodeId}`, {
      includeAuth: false,
    });
  }

  async getLatestStoriverseContent() {
    // Public endpoint - no auth required
    return this.request("/storyverse/latest", { includeAuth: false });
  }

  /**
   * Clear Storyverse discovery caches so a moderation hide/show is visible
   * immediately (without waiting for the 5–10 minute TTL or a hard refresh).
   */
  invalidateStoryverseDiscoveryCaches() {
    const keys = [
      "storyverse_stories",
      "pixley_mixes",
      "storyverse_shows",
    ];
    for (const key of keys) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // ignore quota / private-mode failures
      }
    }
    logger.log("🧹 Cleared Storyverse discovery session caches");
  }

  async getStoriverseStories() {
    // Public endpoint - no auth required
    // Cache for 5 minutes to improve performance
    const cacheKey = "storyverse_stories";
    const cached = this.getSessionCached(cacheKey);
    if (cached) {
      logger.log("✅ Using cached storyverse stories");
      return cached;
    }

    const result = await this.request("/storyverse/stories", {
      includeAuth: false,
    });
    if (result?.status) {
      this.setSessionCached(cacheKey, result);
    }
    return result;
  }

  async getPixleyMixes() {
    // Public endpoint - no auth required
    // Cache for 10 minutes (changes less frequently)
    const cacheKey = "pixley_mixes";
    const cached = this.getSessionCached(cacheKey);
    if (cached) {
      logger.log("✅ Using cached Pixley mixes");
      return cached;
    }

    const result = await this.request("/pixley-mixes", { includeAuth: false });
    if (result?.status) {
      this.setSessionCached(cacheKey, result);
    }
    return result;
  }

  async getForYouStories() {
    return this.request("/stories/for-you");
  }

  async searchStoriesByEmbeddings(query, limit = 10) {
    return this.request("/stories/search", {
      method: "POST",
      body: JSON.stringify({ query, limit }),
    });
  }

  async incrementStoryView(storyId) {
    // Public endpoint - no auth required
    return this.request(`/stories/${storyId}/increment-view`, {
      method: "POST",
      includeAuth: false,
    });
  }

  async incrementAudiobookView(audiobookId) {
    // Public endpoint - no auth required
    return this.request(`/audiobooks/${audiobookId}/increment-view`, {
      method: "POST",
      includeAuth: false,
    });
  }

  /**
   * GET /likes/check — check if user has liked content.
   */
  async checkLike(contentType, contentId) {
    const params = new URLSearchParams({
      content_type: contentType,
      content_id: contentId,
    });
    return this.request(`/likes/check?${params}`);
  }

  /**
   * POST /likes — like content (requires auth).
   */
  async likeContent(contentType, contentId) {
    return this.request("/likes", {
      method: "POST",
      body: JSON.stringify({
        content_type: contentType,
        content_id: contentId,
      }),
    });
  }

  /**
   * DELETE /likes — unlike content (requires auth).
   * Backend expects JSON body: { content_type, content_id }.
   */
  async unlikeContent(contentType, contentId) {
    return this.request("/likes", {
      method: "DELETE",
      body: JSON.stringify({
        content_type: contentType,
        content_id: contentId,
      }),
    });
  }

  /**
   * GET /likes — list user's liked content.
   * Returns array of { content_type: "storybook" | "video", content_id, created_at }.
   */
  async getLikes() {
    return this.request("/likes");
  }

  async sendStoryReadyEmail(storyId) {
    return this.request(`/stories/${storyId}/send-ready-email`, {
      method: "POST",
    });
  }

  // Share methods — anonymous (no JWT); require the share token in ?token=.
  async getSharedCharacter(id, token = null) {
    const url = token
      ? `/shareCharacters/${id}?token=${encodeURIComponent(token)}`
      : `/shareCharacters/${id}`;
    return this.request(url, { includeAuth: false });
  }

  async getSharedEpisode(id, token = null) {
    const url = token
      ? `/shareEpisodes/${id}?token=${encodeURIComponent(token)}`
      : `/shareEpisodes/${id}`;
    return this.request(url, { includeAuth: false });
  }

  // Stats methods
  async getStats() {
    return this.request("/stats");
  }

  // User profile methods
  async updateUsername(userId, newUsername) {
    const formData = new FormData();
    formData.append("username", newUsername);

    return this.uploadFileToEndpoint(`/users/${userId}/username`, formData, {
      method: "PUT",
    });
  }

  async updatePlatform(userId, platform) {
    const formData = new FormData();
    formData.append("platform", platform);

    return this.uploadFileToEndpoint(`/users/${userId}/platform`, formData, {
      method: "PUT",
    });
  }

  async updateProfilePicture(userId, profilePictureUrl) {
    const formData = new FormData();
    formData.append("profile_picture", profilePictureUrl);

    return this.uploadFileToEndpoint(
      `/users/${userId}/profile-picture`,
      formData,
      {
        method: "PUT",
      },
    );
  }

  async addUserDataTag(userId, tag) {
    const formData = new FormData();
    formData.append("tag", tag);

    return this.uploadFileToEndpoint(`/users/${userId}/data-tags`, formData, {
      method: "POST",
    });
  }

  async getUserStats(userId) {
    return this.request(`/users/${userId}/stats`);
  }

  async updateStoryProgress(userId, payload) {
    return this.request(`/users/${userId}/stats/story-progress`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateStorybookProgress(userId, payload) {
    return this.request(`/users/${userId}/stats/storybook-progress`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getSubscriptionStatus(userId, { force = false } = {}) {
    // force=true busts the backend's short resolve cache too (fresh
    // RevenueCat lookup) — used right after purchases.
    return this.request(
      `/users/${userId}/subscription-status${force ? "?force=true" : ""}`,
    );
  }

  /**
   * POST /users/:userId/cancel-subscription
   * Body: { cancel_immediately: boolean }
   */
  // Cancel flow (backend routers/cancel_flow_router.py): what they've made,
  // why they're leaving, the "next month free" offer, then a one-tap cancel.
  async getCancelFlow() {
    return this.request("/subscription/cancel-flow");
  }

  async startCancelFlow(reason, note) {
    return this.request("/subscription/cancel-flow/start", {
      method: "POST",
      body: JSON.stringify({ platform: "web", reason, note: note || null }),
    });
  }

  async acceptCancelOffer(sessionId) {
    return this.request("/subscription/cancel-flow/accept-offer", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  async acceptCancelDowngrade(sessionId) {
    return this.request("/subscription/cancel-flow/accept-downgrade", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  async confirmCancelFlow(sessionId) {
    return this.request("/subscription/cancel-flow/cancel", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  async cancelFlowOutcome(sessionId, outcome) {
    return this.request("/subscription/cancel-flow/outcome", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, outcome }),
    });
  }

  async cancelSubscription(userId, cancelImmediately = false) {
    return this.request(`/users/${userId}/cancel-subscription`, {
      method: "POST",
      body: JSON.stringify({ cancel_immediately: cancelImmediately }),
    });
  }

  // Profile Management Methods
  async getProfiles() {
    return this.request("/profiles");
  }

  async getDefaultProfile() {
    return this.request("/profiles/default");
  }

  async getProfile(profileId) {
    return this.request(`/profiles/${profileId}`);
  }

  async createProfile(
    name,
    avatarUrl = null,
    starLimit = null,
    isChild = false,
    allowStoryversePosting = false,
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (avatarUrl) formData.append("avatar_url", avatarUrl);
    if (starLimit !== null && starLimit !== "" && !isNaN(parseInt(starLimit))) {
      formData.append("star_limit", starLimit.toString());
    }
    formData.append("is_child", isChild.toString());
    if (isChild) {
      formData.append(
        "allow_storyverse_posting",
        allowStoryversePosting.toString(),
      );
    }

    return this.request("/profiles", {
      method: "POST",
      body: formData,
      headers: { "Content-Type": undefined }, // Explicitly remove Content-Type
    });
  }

  async updateProfile(profileId, updates) {
    const formData = new FormData();
    if (updates.name) formData.append("name", updates.name);
    if (updates.avatarUrl) formData.append("avatar_url", updates.avatarUrl);
    if (
      updates.starLimit !== undefined &&
      updates.starLimit !== null &&
      updates.starLimit !== "" &&
      !isNaN(parseInt(updates.starLimit))
    ) {
      formData.append("star_limit", updates.starLimit.toString());
    }
    if (updates.isChild !== undefined)
      formData.append("is_child", updates.isChild.toString());
    if (updates.allowStoryversePosting !== undefined)
      formData.append(
        "allow_storyverse_posting",
        updates.allowStoryversePosting.toString(),
      );

    return this.request(`/profiles/${profileId}`, {
      method: "PUT",
      body: formData,
      headers: { "Content-Type": undefined }, // Explicitly remove Content-Type
    });
  }

  async deleteProfile(profileId) {
    return this.request(`/profiles/${profileId}`, {
      method: "DELETE",
    });
  }

  // Stripe checkout methods
  async createStripeCheckoutSession(
    userId,
    userEmail,
    tier = "pro",
    billingPeriod = "monthly",
    returnPath = null,
    offer = null, // "winback": 33% off 3 months for a lapsed family (backend re-checks)
  ) {
    // Map tier names to match backend expectations
    const tierMap = {
      free: "free",
      starter: "starter",
      plus: "pro", // Frontend uses 'plus', backend uses 'pro'
      pro: "pro",
      family: "family",
      unlimited: "unlimited",
    };

    const mappedTier = tierMap[tier.toLowerCase()] || "pro";

    return this.request("/create-stripe-checkout-session", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        user_email: userEmail,
        tier: mappedTier,
        billing_period: billingPeriod,
        ...(offer ? { offer } : {}),
        // Send the user back to wherever they were when they hit the wall
        // (mid-creation pages restore their saved draft on return). Only
        // same-origin paths are honored; anything else falls back to
        // /settings.
        success_url: (() => {
          const rp =
            typeof returnPath === "string" &&
            returnPath.startsWith("/") &&
            !returnPath.startsWith("//")
              ? returnPath
              : "/settings";
          const sep = rp.includes("?") ? "&" : "?";
          // {CHECKOUT_SESSION_ID} is filled in by Stripe on the redirect (literal braces)
          return `${window.location.origin}${rp}${sep}upgraded=true&tier=${mappedTier}&billing=${billingPeriod}&session_id={CHECKOUT_SESSION_ID}`;
        })(),
        cancel_url: (() => {
          const rp =
            typeof returnPath === "string" &&
            returnPath.startsWith("/") &&
            !returnPath.startsWith("//")
              ? returnPath
              : "/settings";
          const sep = rp.includes("?") ? "&" : "?";
          return `${window.location.origin}${rp}${sep}cancelled=true`;
        })(),
      }),
    });
  }

  async createStripeStarPurchaseSession(userId, userEmail, productId) {
    logger.log("🛒 Creating RevenueCat checkout for star purchase:", {
      userId,
      userEmail,
      productId,
    });
    return this.request("/create-stripe-checkout-product-id", {
      method: "POST",
      body: JSON.stringify({
        product_id: productId,
        user_id: userId,
        user_email: userEmail,
        success_url: `${window.location.origin}/settings?tab=shop&purchased=true&product=${productId}`,
        cancel_url: `${window.location.origin}/settings?tab=shop&cancelled=true`,
      }),
    });
  }

  async updateStripeSubscription(userId, tier, billingPeriod = "monthly") {
    // Map tier names to match backend expectations
    const tierMap = {
      free: "free",
      starter: "starter",
      plus: "pro", // Frontend uses 'plus', backend uses 'pro'
      pro: "pro",
      family: "family",
      unlimited: "unlimited",
    };

    const mappedTier = tierMap[tier.toLowerCase()] || "pro";

    return this.request("/update-stripe-subscription", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        tier: mappedTier,
        billing_period: billingPeriod,
      }),
    });
  }

  async createStripePortalSession(userId) {
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("return_url", `${window.location.origin}/settings`);

    return this.request("/create-stripe-portal-session", {
      method: "POST",
      body: formData,
      headers: {}, // Let fetch auto-set Content-Type for FormData
    });
  }

  async syncSubscription(userId) {
    return this.request("/sync-subscription", {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: "DELETE",
      body: JSON.stringify({ confirmation: "DELETE_MY_ACCOUNT" }),
    });
  }

  // Personalization methods
  async getPersonalizationShows() {
    return this.request("/personalization-shows");
  }

  async getUserPersona(userId) {
    return this.request(`/users/${userId}/persona`);
  }

  // End a web trial early: bills the plan now, weekly stars follow via the
  // RC NORMAL webhook grant. 404 = no trialing subscription on the account.
  async convertTrialNow(userId) {
    return this.request(`/users/${userId}/convert-trial-now`, {
      method: "POST",
    });
  }

  // Merge a patch into users.personalization (jsonb). Same endpoint/shape the
  // iOS app uses for parental consent: {parental_consent: {agreed, agreed_at}}
  async updateUserPersonalization(userId, patch) {
    return this.request(`/users/${userId}/personalization`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  async updateUserPersona(userId, persona) {
    // Create FormData for multipart/form-data request
    const formData = new FormData();
    formData.append("persona", JSON.stringify(persona));

    return this.uploadFileToEndpoint(`/users/${userId}/persona`, formData, {
      method: "PUT",
    });
  }

  // Character editing methods
  async editCharacterPreview(characterId, editPrompt) {
    const formData = new FormData();
    formData.append("edit_prompt", editPrompt);

    return this.uploadFileToEndpoint(
      `/characters/${characterId}/edit-preview`,
      formData,
    );
  }

  async editImagePreview(imageData, editPrompt) {
    const formData = new FormData();
    formData.append("edit_prompt", editPrompt);
    formData.append("image_file", imageData);

    return this.uploadFileToEndpoint("/edit-image-preview", formData);
  }

  async saveEditedCharacter(characterId, editedImageData, editHistory = "") {
    const formData = new FormData();
    formData.append("edited_image_data", editedImageData);
    formData.append("edit_history", editHistory);

    return this.uploadFileToEndpoint(
      `/characters/${characterId}/save-edited`,
      formData,
    );
  }

  // Star shop methods
  async getStarAmountForProduct(productId) {
    return this.request(`/getStarAmountForProduct/${productId}`, {
      includeAuth: false,
    });
  }

  async getStarShopProducts() {
    return this.request("/star-shop-products", {
      includeAuth: false,
    });
  }

  async getCosts(userId, subscriptionType) {
    return this.request(
      `/costs?user_id=${encodeURIComponent(
        userId,
      )}&subscription_type=${encodeURIComponent(subscriptionType)}`,
    );
  }

  async useStars(amount, reason = "unknown") {
    const payload = {
      amount: Number(amount),
      reason,
    };
    console.log("Calling /useStars with:", { amount: payload.amount });
    return this.request("/useStars", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getParentVoice() {
    return this.request("/voices/parent");
  }

  async cloneParentVoice(audioFiles) {
    const formData = new FormData();
    audioFiles.forEach((file) => {
      formData.append("audio_files", file);
    });
    return this.uploadFileToEndpoint("/voices/clone-parent", formData);
  }

  /**
   * Fast ElevenLabs TTS for games (narrator / character lines).
   * POST /elevenlabs/quick-tts — returns raw base64 MP3 string, or null on failure.
   */
  async quickTTS(text, voiceId) {
    const trimmed = typeof text === "string" ? text.trim() : "";
    if (!trimmed || !voiceId) return null;
    try {
      const response = await this.request("/elevenlabs/quick-tts", {
        method: "POST",
        body: JSON.stringify({ text: trimmed, voice_id: voiceId }),
      });
      const base64 =
        response?.data?.audio_base_64 || response?.audio_base_64 || null;
      return typeof base64 === "string" && base64.length > 0 ? base64 : null;
    } catch (error) {
      logger.warn("quickTTS failed:", error?.message || error);
      return null;
    }
  }

  /**
   * POST /storyverse-analytics/events — Pixley Originals playback milestones.
   */
  async sendStoryverseAnalyticsEvent(payload) {
    return this.request("/storyverse-analytics/events", {
      method: "POST",
      body: JSON.stringify(payload),
      includeAuth: true,
    });
  }

  async getStoryverseEpisodeAnalyticsSummary(episodeId) {
    return this.request(`/storyverse-analytics/episodes/${episodeId}/summary`, {
      includeAuth: false,
    });
  }

  // Analytics methods
  async updateUserAnalytics(userId, analyticsData, analyticsType) {
    const formData = new FormData();
    formData.append("analytics_data", JSON.stringify(analyticsData));
    formData.append("analytics_type", analyticsType); // "onboarding" or "engagement"

    return this.uploadFileToEndpoint(`/users/${userId}/analytics`, formData, {
      method: "PUT",
    });
  }

  async getPMFShouldShow() {
    return this.request("/pmf/should-show");
  }

  async submitPMFFeedback(data) {
    return this.request("/pmf/submit", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async dismissPMF() {
    return this.request("/pmf/dismiss", {
      method: "POST",
    });
  }

  async generateIntroVideo(username) {
    const formData = new FormData();
    formData.append("username", username);
    return this.uploadFileToEndpoint("/generate-intro-video", formData);
  }

  async getIntroVideoStatus(introId) {
    return this.request(`/intro-video/${introId}`);
  }

  /**
   * Internal Storyverse moderation — JWT of an internal Pixley user required.
   * GET /internal/stories/{id}/storyverse-visibility
   * Returns 403 for non-internal users (frontend must hide the control).
   */
  async getStoryverseVisibility(storyId) {
    if (!storyId) {
      return { status: false, error: "Story id is required" };
    }
    return this.request(`/internal/stories/${storyId}/storyverse-visibility`, {
      includeAuth: true,
    });
  }

  /**
   * Internal Storyverse moderation toggle — JWT of an internal Pixley user.
   * PATCH /internal/stories/{id}/storyverse-visibility
   */
  async setStoryverseVisibility(storyId, showInStoryverse) {
    if (!storyId) {
      return { status: false, error: "Story id is required" };
    }
    const result = await this.request(
      `/internal/stories/${storyId}/storyverse-visibility`,
      {
        method: "PATCH",
        includeAuth: true,
        body: JSON.stringify({ show_in_storyverse: Boolean(showInStoryverse) }),
      },
    );
    // Drop stale Hero / Trending / Adventure / Mystery lists immediately.
    if (result?.status === "success") {
      this.invalidateStoryverseDiscoveryCaches();
    }
    return result;
  }
}

// Changes whenever this module is re-evaluated (including Fast Refresh), so we
// can replace a stale APIClient instance that is missing newly added methods.
const API_CLIENT_MODULE_GENERATION = Date.now();

function cloneApiClientState(previous, next) {
  if (!previous || !next) return next;
  if (previous.token) next.token = previous.token;
  if (previous.activeProfileId) next.activeProfileId = previous.activeProfileId;
  return next;
}

export const APIProvider = ({ children }) => {
  const [apiClient, setApiClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const generationRef = React.useRef(API_CLIENT_MODULE_GENERATION);

  useEffect(() => {
    const client = new APIClient();
    setApiClient(client);
    setLoading(false);

    // Set global API client for AuthContext and testing
    window.pixleyApiClient = client;

    // Also make it available for development testing
    if (process.env.NODE_ENV === "development") {
      window.testTokenExpiration = () => client.testTokenExpiration();
    }

    // Test API connection
    const testConnection = async () => {
      try {
        const result = await client.healthCheck();
        if (result.status) {
          logger.log("✅ Backend API connection successful");
        } else {
          logger.warn("⚠️ Backend API connection test failed:", result.error);
        }
      } catch (error) {
        logger.warn("⚠️ Backend API connection test failed:", error.message);
      }
    };

    testConnection();
  }, []);

  // Fast Refresh preserves React state, so the context can keep an old
  // APIClient instance whose prototype predates methods like
  // downloadAudiobookPdf. When this module hot-reloads, recreate the client
  // (preserving auth/profile) and always rebind the latest prototype.
  useEffect(() => {
    if (!apiClient) return;

    Object.setPrototypeOf(apiClient, APIClient.prototype);

    const moduleReloaded = generationRef.current !== API_CLIENT_MODULE_GENERATION;
    const missingPdfMethod = typeof apiClient.downloadAudiobookPdf !== "function";
    const missingStoryverseModeration =
      typeof apiClient.getStoryverseVisibility !== "function" ||
      typeof apiClient.setStoryverseVisibility !== "function";

    if (!moduleReloaded && !missingPdfMethod && !missingStoryverseModeration) {
      window.pixleyApiClient = apiClient;
      return;
    }

    generationRef.current = API_CLIENT_MODULE_GENERATION;
    const next = cloneApiClientState(apiClient, new APIClient());
    window.pixleyApiClient = next;
    setApiClient(next);
    logger.log("🔄 APIClient refreshed after module update");
  });

  const value = {
    apiClient,
    loading,
  };

  return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
};
